/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { trackGoal as trackFathomGoal } from 'fathom-client'
import Image from 'next/image'
import LegacyImage from 'next/legacy/image'
import { StandaloneLink, Card } from '@hashicorp/mds-react/components'
import PartnerSubnav from 'views/partners/components/subnav'
import type { SystemsIntegratorPageFieldsFragment } from 'types/dato'
import s from './style.module.css'

function LogoContainer({ logo, className, partnerWebsiteUrl }) {
	if (!logo) return null
	if (!partnerWebsiteUrl) {
		return (
			<div className={`${s.logoContainer} ${className}`}>
				<Image {...logo} alt={logo.alt || ''} />
			</div>
		)
	} else {
		return (
			<a
				className={`${s.logoContainer} ${className}`}
				href={partnerWebsiteUrl}
				onClick={() => trackExternalPartnerWebsiteClick()}
				rel="noreferrer"
				target="_blank"
			>
				<Image {...logo} alt={logo.alt || ''} />
			</a>
		)
	}
}

function trackExternalPartnerWebsiteClick() {
	// ID here is from hashicorp.com Fathom site - 'Visited external partner website' event
	trackFathomGoal('EUKEDSKC', 0)
}

export function SystemsIntegratorView({
	tier,
	name,
	description,
	logo,
	websiteUrl,
	systemsIntegratorCompetencies,
	awardsAndRecognition,
	certifiedImplementationProducts,
	relatedResources,
	regionsServed,
}: SystemsIntegratorPageFieldsFragment) {
	const globalRegions = Object.keys(regionsServed)

	return (
		<>
			<PartnerSubnav />
			<section className={s.content}>
				<div className={s.hero}>
					<div className="g-grid-container">
						<div className={s.heroContainer}>
							<div className={s.heroContent}>
								{tier && <p className={s.eyebrow}>Partner Tier: {tier}</p>}
								<h1 className={s.systemsIntegratorName}>{name}</h1>
								<p className={s.companyDescription}>{description}</p>
							</div>
							<div className={s.heroSecondary}>
								<LogoContainer
									logo={logo}
									className={s.desktopLogo}
									partnerWebsiteUrl={websiteUrl}
								/>
								{websiteUrl && (
									<div className={s.partnerWebLink}>
										<StandaloneLink
											text="Visit partner website"
											href={websiteUrl}
											onClick={() => trackExternalPartnerWebsiteClick()}
											icon="arrow-right"
											iconPosition="trailing"
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<Image
						src={require('components/_page-components/partners/images/partner-hero-graphic.svg')}
						className={s.heroGraphic}
						alt=""
						width={210}
						height={362}
					/>
				</div>
				<div className={s.heroSecondary}>
					<LogoContainer
						logo={logo}
						className={s.mobileLogo}
						partnerWebsiteUrl={websiteUrl}
					/>
				</div>
				<div className={s.mainContent}>
					<section className={s.regionsServed}>
						<h2 className={s.regionsServedHeading}>Regions served</h2>
						{globalRegions.length > 0 ? (
							<ul className={s.globalRegionsList}>
								{globalRegions.map((globalRegion) => {
									const localRegions = regionsServed[globalRegion]

									return (
										<li key={globalRegion}>
											<h3 className={s.globalRegionHeading}>{globalRegion}</h3>
											<ul
												// style={{'--row-count': } as React.CSSProperties}
												className={s.localRegionsList}
											>
												{localRegions.map((localRegion) => {
													return (
														<li className={s.localRegion} key={localRegion}>
															{localRegion}
														</li>
													)
												})}
											</ul>
										</li>
									)
								})}
							</ul>
						) : null}
					</section>
					<section className={s.badges}>
						{/* TODO Replace text items in these lists with badges */}
						{/* TODO Uncomment once Systems Integrator Competencies are implemented in the SI records */}
						{systemsIntegratorCompetencies?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Systems integrator competencies
								</h2>
								<ul className={s.badgesList}>
									{systemsIntegratorCompetencies.map(({ badge }, stableIdx) => {
										return (
											// eslint-disable-next-line react/no-array-index-key
											<li key={stableIdx}>
												<LegacyImage
													src={badge.url}
													alt={badge.alt}
													width={127}
													height={147}
												/>
											</li>
										)
									})}
								</ul>
							</div>
						) : null}
						{awardsAndRecognition?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Awards & recognition
								</h2>
								<ul className={s.badgesList}>
									{awardsAndRecognition.map((highlight, stableIdx) => {
										return (
											// eslint-disable-next-line react/no-array-index-key
											<li key={stableIdx}>
												<LegacyImage
													src={highlight.url}
													alt={highlight.alt}
													width={127}
													height={147}
												/>
											</li>
										)
									})}
								</ul>
							</div>
						) : null}
						{certifiedImplementationProducts?.length > 0 ? (
							<div className={s.badgesCategory}>
								<h2 className={s.badgesCategoryHeading}>
									Certified implementation partner
								</h2>
								<ul className={s.badgesList}>
									{certifiedImplementationProducts.map((product) => {
										return (
											<li
												className={s.certificationBadge}
												key={product.product}
											>
												<LegacyImage
													src={product.badge.url}
													alt={product.badge.alt}
													width={127}
													height={147}
												/>
											</li>
										)
									})}
								</ul>
							</div>
						) : null}
					</section>

					{relatedResources.length > 0 ? (
						<section className={s.relatedResources}>
							<h2 className={s.relatedResourcesHeading}>Related resources</h2>
							<div className={s.relatedResourcesList}>
								{relatedResources.map((resource) => {
									return (
										<Card
											key={resource.linkText}
											href={resource.url}
											content={{ heading: resource.linkText, showArrow: true }}
										/>
									)
								})}
							</div>
							<div className={s.relatedResourcesCta}>
								<StandaloneLink
									text="View all resources"
									href={`/resources`}
									icon="arrow-right"
									iconPosition="trailing"
								/>
							</div>
						</section>
					) : null}
				</div>
			</section>
		</>
	)
}
